import get from 'lodash/get';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { AppContext } from '../../../../App';
import { isCurrentUserAdmin } from '../../../../service/AccountService';
import { canEditReport } from '../../../../service/PermissionService';
import { createEmptyReport, saveReport, deleteReport } from '../../../../service/ReportService';
import { toastError, toastSuccess } from '../../../../ui/ToastHelper';
import DialogEditing from './components/DialogEditing';
import ReportItem from './components/ReportItem';

ReportsPane.propTypes = {
    techFormData: PropTypes.shape({
        reportsList: PropTypes.array,
        line: PropTypes.number
    }),
    isCreatingReport: PropTypes.bool,
    onReportCreationFinished: PropTypes.func,
    onReportDeleted: PropTypes.func
};

ReportsPane.defaultProps = {
    techFormData: {
        reportsList: [],
        line: 0
    },
    isCreatingReport: false
};

export default function ReportsPane({ techFormData, isCreatingReport, onReportCreationFinished, onReportDeleted }) {
    const appContext = React.useContext(AppContext);
    const [isEditingReport, setIsEditingReport] = useState(false);

    const [reportList, setReportList] = useState(null);
    useEffect(() => setReportList(techFormData != null ? techFormData.reportsList : []), [techFormData]);

    const [editedReport, setEditedReport] = useState(null);

    useEffect(() => {
        if (isCreatingReport === true) {
            const newReport = get(reportList, '0') == null ? createEmptyReport() : reportList[0];
            setEditedReport({ ...newReport, pk: null, date: null });
            setIsEditingReport(true);
        }
    }, [isCreatingReport, reportList]);

    const onSaveReport = (linesData, userPk) => {
        // Extract date from linesData
        const reportDate = linesData.date;
        const reportUser = userPk;  // User pk comes as separate parameter

        // Validate that date and userPk are always present
        if (!reportDate || reportDate === '') {
            toastError('Report date is required');
            return;
        }

        if (!reportUser || reportUser === '') {
            toastError('User is required');
            return;
        }

        setIsEditingReport(false);

        // Remove date from linesData before passing to saveReport
        const { date, ...cleanedLinesData } = linesData;

        saveReport(editedReport.pk, techFormData, cleanedLinesData, (savedReport) => {
            let newList = [...reportList];
            if (editedReport.pk != null) {
                // update existing report in the list
                newList = reportList.map((report) =>
                    report.pk === savedReport.pk ? savedReport : report
                );
            } else {
                // add the new report in front of the list
                newList.unshift(savedReport);
            }
            setReportList(newList);
        }, reportDate, reportUser);
        onReportCreationFinished();
    };

    const onCancelEditing = () => {
        setIsEditingReport(false);
        onReportCreationFinished();
    };

    const onReportEdit = (index) => {
        setEditedReport(reportList[index]);
        setIsEditingReport(true);
    };

    const onReportDelete = (reportPk) => {
        deleteReport(reportPk, () => {
            // Notify parent component to update the techFormData
            // This will remove the report and its linked photos
            if (onReportDeleted) {
                onReportDeleted(reportPk);
            }
            toastSuccess('Report deleted successfully');
        });
    };

    const dialogOpen = isEditingReport || isCreatingReport;

    return (
        <>
            <div className='list-container'>
                {reportList &&
                    reportList.map((report, index) => (
                        <ReportItem
                            className='item'
                            linesCount={techFormData.line}
                            reportData={report}
                            cacType={appContext.cacType}
                            isEditable={canEditReport(index, report.techId)}
                            onEdit={() => onReportEdit(index)}
                            onDelete={onReportDelete}
                            photosList={techFormData.photosList}
                            contractHarvest={techFormData.contractHarvest}
                            key={index}
                        />
                    ))}
            </div>
            {dialogOpen && techFormData != null && (
                <DialogEditing
                    isOpen
                    reportData={editedReport}
                    cacType={appContext.cacType}
                    linesCount={techFormData.line}
                    contractHarvest={techFormData.contractHarvest}
                    onSave={onSaveReport}
                    onCancel={onCancelEditing}
                    isNewReport={editedReport?.pk == null}
                    isAdmin={isCurrentUserAdmin()}
                />
            )}
        </>
    );
}
